import type { CSSProperties, ReactNode } from 'react';

/**
 * Bölüm başlıklarındaki vurgu öbeği.
 *
 * Mesaj dosyasında `<hl>…</hl>` ile işaretlenir, bileşende `t.rich`'e bu
 * fonksiyon geçirilir:
 *
 * ```tsx
 * <h2 className="mod-title">{t.rich('title', { hl })}</h2>
 * ```
 *
 * `<mark>` bilinçli: öbek gerçekten vurgulanmış metin, dekoratif bir span
 * değil. Erişilebilir isim etkilenmiyor — `aria-labelledby` başlığın düz
 * metnini okuyor, işaretleme araya girmiyor.
 *
 * Görünüm globals.css → `.hl`. Yeşil şeridin BOYU ve YERİ oradan değil
 * buradan geliyor; nedeni aşağıda.
 */

/* ---------------------------------------------------------------------------
   Şerit neden burada hesaplanıyor

   Yeşil zemin, satır içi öğenin kendi kutusu değil (o kutu font metriğinden
   geliyor ve harflere göre ortalı değil); boyu ve yeri elle verilen bir
   `linear-gradient` şeridi. Sabit tek bir şerit ise HER öbekte ortalı olamıyor:
   inişi olan bir öbek ("tek panelde") altta yer isterken, inişi olmayan bir
   öbek ("izni sizden alır.") o yeri boş bırakıyor ve kutu alta kaçmış görünüyor.

   Bu yüzden şerit öbeğin KENDİ harflerinden hesaplanıyor: en yükseğinin üstünde
   ne kadar boşluk varsa en alçağının altında da o kadar var.

   Değerler ölçüldü, tahmin değil — Plus Jakarta Sans 500, canvas
   `measureText().actualBoundingBox*`, em cinsinden. Yalnızca istisnalar yazılı;
   listede olmayan her karakter için taban değerler kullanılıyor.
--------------------------------------------------------------------------- */

/** Taban çizgisinin ÜSTÜ. Liste dışındaki her şey için taban: büyük harf boyu. */
const CAP = 0.7566;
const ASCENT: Record<string, number> = {
  Û: 1.0538,
  Â: 1.0268,
  Î: 1.0268,
  Ğ: 1.0268,
  Ö: 0.9997,
  Ü: 0.9997,
  İ: 0.9727,
  â: 0.8376,
  î: 0.8376,
  û: 0.8376,
  ğ: 0.8106,
  b: 0.7836,
  d: 0.7836,
  h: 0.7836,
  k: 0.7836,
  l: 0.7836,
  ö: 0.7836,
  ü: 0.7836,
  Ş: 0.7836,
  '“': 0.7836,
};

/** Taban çizgisinin ALTI. Listede olmayan hiçbir karakter aşağı inmiyor. */
const DESCENT: Record<string, number> = {
  g: 0.2432,
  ç: 0.2432,
  ğ: 0.2432,
  ş: 0.2432,
  Ç: 0.2432,
  Ş: 0.2432,
  '@': 0.2432,
  j: 0.2162,
  p: 0.2162,
  q: 0.2162,
  y: 0.2162,
  '(': 0.1891,
  ')': 0.1891,
  ',': 0.1621,
  ';': 0.1621,
  '[': 0.1621,
  ']': 0.1621,
  '/': 0.1351,
  Q: 0.0811,
  Ğ: 0.027,
};

/** Harf ucu ile şerit kenarı arası. İki yanda EŞİT — ortalamanın kendisi bu. */
const PAD = 0.1;
/** `.hl` taşıyan başlıkların satır arası (globals.css → `--section-title`). */
const LINE = 1.34;
/** Sarılan iki şerit arasında kalması gereken boşluk. */
const MIN_GAP = 0.11;
/** Font kutusunun üst kenarı, taban çizgisinden. */
const BOX_TOP = 1.0268;
/** `.hl`'nin dikey dolgusu (globals.css) — şeridin font kutusu dışına
    taşabildiği pay. Dolgu olmasa şerit orada kırpılırdı; satır içi öğede dikey
    dolgu satır yüksekliğini etkilemediği için yerleşim bozulmuyor. */
const PAD_BLOCK = 0.2;

/**
 * Öbeğin harflerine göre şeridin boyu (`--hl-h`) ve üstten kaydırması
 * (`--hl-y`). Kaydırma dolgu kutusunun üst kenarından ölçülüyor —
 * `background-origin` varsayılanı orası.
 */
function band(text: string | null): CSSProperties {
  /* Metin düz bir dize değilse (iç içe işaretleme) ölçemiyoruz: en geniş
     şeridi kullan. Fazla yer ayırmak kırpmaktan iyidir. */
  let asc = text === null ? 0.7836 : CAP;
  let desc = text === null ? 0.2432 : 0;
  for (const ch of text ?? '') {
    asc = Math.max(asc, ASCENT[ch] ?? 0);
    desc = Math.max(desc, DESCENT[ch] ?? 0);
  }

  /* Şerit satır arasından taşarsa sarılan parçalar üst üste biner. Öbek hem
     çok yüksek hem çok derin harf taşıyorsa ("Ödeme yapın") boşluk kısılıyor —
     iki yanda birden kısıldığı için ortalama bozulmuyor. */
  const pad = Math.min(PAD, Math.max(0, (LINE - asc - desc - MIN_GAP) / 2));

  return {
    '--hl-h': `${(asc + desc + 2 * pad).toFixed(4)}em`,
    '--hl-y': `${(BOX_TOP + PAD_BLOCK - asc - pad).toFixed(4)}em`,
  } as CSSProperties;
}

/** `t.rich` düz metni dize olarak veriyor; iç içe işaretlemede vermiyor. */
function plain(chunks: ReactNode): string | null {
  if (typeof chunks === 'string') return chunks;
  if (Array.isArray(chunks) && chunks.every((c) => typeof c === 'string')) {
    return chunks.join('');
  }
  return null;
}

export const hl = (chunks: ReactNode) => (
  <mark className="hl" style={band(plain(chunks))}>
    {chunks}
  </mark>
);
